import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Search, Trash2, Package, Euro, Building2, List as ListIcon, Table as TableIcon, CheckCircle2, XCircle, Lock, Eye, X, RotateCcw, Clock, Send, ArrowRight, Settings, Calendar, RefreshCw
} from 'lucide-react';
import { ColumnConfigurator, ColumnConfig } from '@/components/ui/ColumnConfigurator';
import { ModuleHeader } from '@/components/layout/ModuleHeader';
import { useAuth } from '../../contexts/AuthContext';
import { useAlert } from '../../components/AlertProvider';
import { useConfirm } from '../../components/ConfirmDialog';
import api from '../../services/api';
import TierConfigModal from '../../components/TierConfigModal';
import UnitTypeTierModal from '../../components/UnitTypeTierModal';
import BpuModificationModal from '../../components/BpuModificationModal';
import ApporteurReductionRequest from '../../components/ApporteurReductionRequest';
import PendingRequestsList from '../../components/PendingRequestsList';
import BpuCreationModal from '../../components/BpuCreationModal';
import BpuViewModal from '../../components/BpuViewModal';
import ReductionRequestsModal from '../../components/ReductionRequestsModal';
import BpuProductTierModal from '../../components/BpuProductTierModal';

interface Client {
  id: number;
  company_name: string;
  marque?: string | null;
  email: string;
  client_type: 'independent' | 'central' | 'centrale' | 'member' | 'pharmacie' | 'parapharmacie' | 'magasin_bio' | 'therapeute';
}

interface Consumable {
  id: number;
  title: string;
  marque?: string | null;
  base_price: number;
  currency: string;
  unit: string;
  unit_id: number;
}

interface BuyerPricingItem {
  id: number;
  consumable_id: number;
  custom_price: number;
  discount_percentage: number | null;
  free_units?: number | null;
  tiers?: Array<{
    id: number;
    tier_number: number;
    min_quantity: number;
    max_quantity?: number | null;
    discount_percentage?: number | null;
    free_units?: number | null;
    created_at: string;
    updated_at: string;
  }>;
  created_at: string;
  updated_at: string;
}

interface ClientPricing {
  id: number;
  reference_number: string;
  name: string;
  client_id: number;
  description: string | null;
  is_active: boolean;
  status: 'draft' | 'validated' | 'archived';
  version: number;
  parent_bpu_id: number | null;
  payment_term: string;
  activated_at: string;
  deactivated_at: string | null;
  validated_at: string | null;
  archived_at: string | null;
  pricing_items: BuyerPricingItem[];
  created_by: number;
  created_at: string;
  updated_at: string;
  Client?: Client;
}

interface BuyerPricingListItem {
  id: number;
  reference_number: string;
  name: string;
  client_id: number;
  client_name?: string;
  description: string | null;
  is_active: boolean;
  status: 'draft' | 'validated' | 'archived';
  version: number;
  parent_bpu_id: number | null;
  source_bpu_id: number | null;  // BPU hérité d'une centrale
  payment_term: string;
  activated_at: string;
  deactivated_at: string | null;
  validated_at: string | null;
  archived_at: string | null;
  items_count: number;
  orders_count: number;
  created_by: number;
  created_at: string;
  updated_at: string;
  Client?: Client;
}

// Interface pour les demandes de réduction
interface PriceReductionRequest {
  id: number;
  client_id: number;
  client_pricing_id: number;
  consumable_id: number;
  original_price: number;
  requested_price: number;
  requested_discount_percentage: number | null;
  requested_tiers?: Array<{
    tier_number: number;
    min_quantity: number;
    max_quantity?: number;
    discount_percentage?: number;
    free_units?: number;
  }> | null;
  includes_tier_changes: boolean;
  status: 'pending' | 'approved' | 'rejected';
  request_reason: string | null;
  admin_comment: string | null;
  requested_by: number;
  processed_by: number | null;
  processed_at: string | null;
  created_at: string;
  updated_at: string;
  client_name?: string;
  consumable_title?: string;
  requester_name?: string;
}

const ClientPricingTailwind: React.FC = () => {
  const { user } = useAuth();
  const { showSuccess, showError } = useAlert();
  const { showConfirm } = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchStorageKey = 'bpu_table_search';
  const filterStorageKey = 'bpu_table_filters';
  const storedFilters = (() => {
    try {
      return JSON.parse(localStorage.getItem(filterStorageKey) || '{}') as {
        statusFilter?: 'all' | 'draft' | 'validated' | 'archived';
        buyerTypeFilter?: 'all' | 'independent' | 'centrale' | 'pharmacie' | 'parapharmacie' | 'magasin_bio' | 'therapeute';
        selectedCentralFilter?: number | null;
      };
    } catch (e) {
      console.error('Error parsing saved filters:', e);
      return {};
    }
  })();
  const isManager: boolean = user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'apporteur';
  const isApporteur: boolean = user?.role === 'apporteur';
  const isAdmin: boolean = user?.role === 'super_admin' || user?.role === 'admin';
  const canValidateBpu: boolean = user?.role === 'super_admin';

  const [items, setItems] = useState<BuyerPricingListItem[]>([]);
  const [buyers, setBuyers] = useState<Client[]>([]);
  const [consumables, setConsumables] = useState<Consumable[]>([]);
  const [busy, setBusy] = useState<boolean>(false);
  const [search, setSearch] = useState<string>(() => localStorage.getItem(searchStorageKey) || '');
  const [page, setPage] = useState<number>(1);
  const [pageSize] = useState<number>(200);
  const [total, setTotal] = useState<number>(0);
  const [pages, setPages] = useState<number>(1);
  const [sortField, setSortField] = useState<'reference_number' | 'client_name' | 'name' | 'status' | 'items_count' | 'created_at'>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [columns, setColumns] = useState<ColumnConfig[]>(() => {
    const saved = localStorage.getItem('bpu_table_columns');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Error parsing saved columns:', e);
      }
    }
    return [
      { key: 'reference_number', label: 'Référence', visible: true, sortable: true },
      { key: 'client_name', label: 'Client', visible: true, sortable: true },
      { key: 'name', label: 'Nom BPU', visible: true, sortable: true },
      { key: 'status', label: 'Statut', visible: true, sortable: true },
      { key: 'items_count', label: 'Produits', visible: true, sortable: true },
      { key: 'created_at', label: 'Créé le', visible: true, sortable: true },
      { key: 'actions', label: 'Actions', visible: true, sortable: false },
    ];
  });
  const [showColumnConfigurator, setShowColumnConfigurator] = useState(false);

  const visibleColumnKeys = useMemo(() => {
    return columns.filter(col => col.visible).map(col => col.key);
  }, [columns]);

  // Fonction pour rendre une cellule de header selon la colonne
  const renderTableHeader = (col: ColumnConfig) => {
    const baseClass = "px-4 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-emerald-100 dark:hover:bg-emerald-900/40 transition-colors";

    const handleHeaderClick = () => {
      if (col.sortable) {
        handleSort(col.key as any);
      }
    };

    const renderSortIcon = () => {
      if (!col.sortable) return null;
      if (sortField === col.key) {
        return <span className="text-emerald-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>;
      }
      return null;
    };

    return (
      <th key={col.key} className={baseClass} onClick={handleHeaderClick}>
        <div className="flex items-center gap-1">
          {col.label}
          {renderSortIcon()}
        </div>
      </th>
    );
  };

  // Fonction pour rendre une cellule de body selon la colonne
  const renderTableCell = (col: ColumnConfig, item: BuyerPricingListItem) => {
    switch (col.key) {
      case 'reference_number':
        return (
          <td key={col.key} className="px-4 py-3">
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300">
              {item.reference_number}
            </span>
          </td>
        );
      case 'client_name':
        return (
          <td key={col.key} className="px-4 py-3">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-slate-400" />
              <span className="font-medium text-slate-900 dark:text-slate-100">
                {item.client_name || item.Client?.company_name || 'Client'}
              </span>
            </div>
          </td>
        );
      case 'name':
        return (
          <td key={col.key} className="px-4 py-3">
            <span className="text-slate-900 dark:text-slate-100">{item.name}</span>
          </td>
        );
      case 'status':
        return (
          <td key={col.key} className="px-4 py-3">
            <div className="flex flex-col gap-1">
              {renderStatusBadge(item.status, item.version)}
              {item.is_active ? (
                <div className="flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                  <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-medium">Actif</span>
                </div>
              ) : (
                <div className="flex items-center gap-1">
                  <XCircle className="h-3 w-3 text-slate-400" />
                  <span className="text-[10px] text-slate-400 font-medium">Inactif</span>
                </div>
              )}
            </div>
          </td>
        );
      case 'items_count':
        return (
          <td key={col.key} className="px-4 py-3">
            <div className="flex items-center gap-1.5">
              <Package className="h-4 w-4 text-slate-400" />
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{item.items_count || 0}</span>
            </div>
          </td>
        );
      case 'created_at':
        return (
          <td key={col.key} className="px-4 py-3">
            <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-400">
              <Calendar className="h-3.5 w-3.5" />
              <span className="text-sm">{new Date(item.created_at).toLocaleDateString('fr-FR')}</span>
            </div>
          </td>
        );
      default:
        return null;
    }
  };
  const [statusFilter, setStatusFilter] = useState<'all' | 'draft' | 'validated' | 'archived'>(storedFilters.statusFilter ?? 'all');
  const [buyerTypeFilter, setBuyerTypeFilter] = useState<'all' | 'independent' | 'centrale' | 'pharmacie' | 'parapharmacie' | 'magasin_bio' | 'therapeute'>(storedFilters.buyerTypeFilter ?? 'all');
  const [showInheritedModal, setShowInheritedModal] = useState<boolean>(false);
  const [inheritedBpus, setInheritedBpus] = useState<BuyerPricingListItem[]>([]);
  const [selectedCentralFilter, setSelectedCentralFilter] = useState<number | null>(storedFilters.selectedCentralFilter ?? null);
  const [viewMode, setViewMode] = useState<'list' | 'table'>(() => window.innerWidth < 768 ? 'list' : 'table');
  const [showModal, setShowModal] = useState<boolean>(false);
  const [editing, setEditing] = useState<ClientPricing | null>(null);
  const [showViewModal, setShowViewModal] = useState<boolean>(false);
  const [viewingBpu, setViewingBpu] = useState<BuyerPricingListItem | null>(null);
  const [bpuDetails, setBpuDetails] = useState<ClientPricing | null>(null);
  const [loadingDetails, setLoadingDetails] = useState<boolean>(false);

  // États pour les demandes de réduction
  const [pendingReductions, setPendingReductions] = useState<PriceReductionRequest[]>([]);
  const [showReductionRequestsModal, setShowReductionRequestsModal] = useState<boolean>(false);
  const [reductionRequestReason, setReductionRequestReason] = useState<string>('');
  const [myPendingRequests] = useState<PriceReductionRequest[]>([]);

  // Tier configuration for reduction requests
  const requestedTiers: any[] = [];

  const [form, setForm] = useState<{
    client_id: number | null;
    name: string;
    description: string;
    payment_term: string;
    pricing_type: 'custom_price' | 'discount';
    custom_price: number;
    discount_percent: number;
  }>({
    client_id: null,
    name: '',
    description: '',
    payment_term: '30 jours fin de mois',
    pricing_type: 'custom_price',
    custom_price: 0,
    discount_percent: 0,
  });

  // Multi-product selection states
  const [selectedConsumableIds, setSelectedConsumableIds] = useState<Set<number>>(new Set());
  const [consumablesSearch, setConsumablesSearch] = useState<string>('');
  const [, setExistingPricingForModal] = useState<Record<number, any>>({});
  const [productPricing, setProductPricing] = useState<Record<number, {
    pricing_type: 'custom_price' | 'discount';
    custom_price: number;
    discount_percent: number;
    reduction_mode: 'percent' | 'amount'; // Mode de réduction: pourcentage ou montant
    is_active: boolean; // Produit actif ou non
    free_units?: number; // Unités gratuites (UG)
    tiers?: Array<{
      tier_number: number;
      min_quantity: number;
      max_quantity?: number;
      discount_percentage?: number;
      free_units?: number;
    }>;
  }>>({});

  // Global pricing mode: 'percent' = pourcentage, 'free_units' = unités gratuites
  const [globalPricingMode, setGlobalPricingMode] = useState<'percent' | 'free_units'>('percent');
  const [globalApplyValue, setGlobalApplyValue] = useState<string>('');

  // Tier configuration state
  const [showTierConfig, setShowTierConfig] = useState(false);
  const [selectedProductForTiers, setSelectedProductForTiers] = useState<number | null>(null);

  // BPU Product Tier Modal state
  const [showBpuProductTierModal, setShowBpuProductTierModal] = useState(false);
  const [selectedBpuForTiers, setSelectedBpuForTiers] = useState<ClientPricing | null>(null);

  // Unit-based pricing state
  interface UnitType {
    unit_id: number;
    unit_name: string;
    volume: number;
    batch_size: number;
    product_count: number;
    consumable_ids: number[];
  }
  const [unitTypes, setUnitTypes] = useState<UnitType[]>([]);
  const [showUnitTypeTierModal, setShowUnitTypeTierModal] = useState(false);
  const [selectedUnitType, setSelectedUnitType] = useState<UnitType | null>(null);
  const [, setLoadingUnitTypes] = useState(false);

  // Handle mode switch - enforce single mode by clearing opposite mode data
  const handleModeSwitch = (newMode: 'percent' | 'free_units'): void => {
    if (newMode === globalPricingMode) return;

    // Clear opposite mode data from all products
    const updatedPricing: typeof productPricing = {};
    Object.keys(productPricing).forEach(key => {
      const consumableId = parseInt(key);
      const currentPricing = productPricing[consumableId];

      if (newMode === 'percent') {
        // Switching to percent mode - clear free_units and UG tiers
        updatedPricing[consumableId] = {
          ...currentPricing,
          free_units: undefined,
          tiers: currentPricing.tiers?.filter(tier =>
            tier.discount_percentage !== undefined && tier.discount_percentage !== null
          ) || []
        };
      } else {
        // Switching to free_units mode - clear discount_percent and % tiers
        updatedPricing[consumableId] = {
          ...currentPricing,
          discount_percent: 0,
          tiers: currentPricing.tiers?.filter(tier =>
            tier.free_units !== undefined && tier.free_units !== null
          ) || []
        };
      }
    });

    setProductPricing(updatedPricing);
    setGlobalPricingMode(newMode);
  };

  const load = async (searchTerm: string | undefined = search, nextPage: number = page): Promise<void> => {
    setBusy(true);
    try {
      const term = searchTerm?.trim();
      const [pricingRes, buyersRes, consumablesRes] = await Promise.all([
        api.get('/client-pricing', {
          params: {
            limit: pageSize,
            skip: (nextPage - 1) * pageSize,
            search: term ? term : undefined,
          },
        }),
        api.get('/clients'),
        api.get('/consumables'),
      ]);
      const pricingPayload = pricingRes.data as { items?: BuyerPricingListItem[]; total?: number; page?: number; pages?: number };
      const rawItems: BuyerPricingListItem[] = Array.isArray(pricingPayload?.items) ? pricingPayload.items : [];
      const buyersArr = Array.isArray(buyersRes.data) ? buyersRes.data : [];
      const consumablesArr = Array.isArray(consumablesRes.data) ? consumablesRes.data : [];

      // Build lookup map for buyers
      const buyerById = new Map<number, Client>();
      for (const b of buyersArr) buyerById.set(b.id, b);

      // Enrich BPU items with Client information
      const enriched = rawItems.map((item) => ({
        ...item,
        Client: buyerById.get(item.client_id) || undefined,
        client_name: item.client_name || buyerById.get(item.client_id)?.company_name,
      }));

      setItems(enriched);
      setBuyers(buyersArr);
      setConsumables(consumablesArr);
      setTotal(pricingPayload?.total ?? rawItems.length);
      setPages(pricingPayload?.pages ?? 1);
      setPage(pricingPayload?.page ?? nextPage);

      // Debug: Log client types to see what we actually have
      const clientTypes = [...new Set(buyersArr.map(b => b.client_type))];
      console.log('Available client types:', clientTypes);

      // Also log specific central clients
      const centralClients = buyersArr.filter(b => b.client_type.includes('central') || b.client_type.includes('centrale'));
      console.log('Central/centrale clients:', centralClients.map(c => ({ name: c.company_name, type: c.client_type })));

    } catch (e) {
      console.error('Load error:', e);
    } finally {
      setBusy(false);
    }
  };

  // Charger les BPU hérités (ceux avec source_bpu_id)
  const loadInheritedBpus = async (): Promise<void> => {
    try {
      const pricingRes = await api.get('/client-pricing');
      const rawItems: BuyerPricingListItem[] = Array.isArray(pricingRes.data) ? pricingRes.data : [];

      // Filtrer uniquement les BPU hérités
      const inherited = rawItems.filter(item => item.source_bpu_id !== null);

      // Enrichir avec les informations des buyers
      const buyersRes = await api.get('/clients');
      const buyersArr = Array.isArray(buyersRes.data) ? buyersRes.data : [];
      const buyerById = new Map<number, Client>();
      for (const b of buyersArr) buyerById.set(b.id, b);

      const enriched = inherited.map((item) => ({
        ...item,
        Client: buyerById.get(item.client_id) || undefined,
      }));

      setInheritedBpus(enriched);
    } catch (e) {
      console.error('Error loading inherited BPUs:', e);
      showError('Erreur lors du chargement des BPU hérités');
    }
  };

  useEffect(() => { void load(); }, []);

  useEffect(() => {
    const delay = setTimeout(() => {
      setPage(1);
      void load(search || undefined, 1);
    }, 300);
    return () => clearTimeout(delay);
  }, [search]);

  useEffect(() => {
    localStorage.setItem(searchStorageKey, search);
  }, [search]);

  useEffect(() => {
    localStorage.setItem(filterStorageKey, JSON.stringify({
      statusFilter,
      buyerTypeFilter,
      selectedCentralFilter
    }));
  }, [statusFilter, buyerTypeFilter, selectedCentralFilter]);

  useEffect(() => {
    void load(search || undefined, page);
  }, [page]);

  // Charger les demandes de réduction
  const loadPendingReductions = async (): Promise<void> => {
    try {
      const response = await api.get('/price-reduction-requests', {
        params: { status_filter: isAdmin ? 'pending' : undefined }
      });
      setPendingReductions(Array.isArray(response.data) ? response.data : []);
    } catch (e) {
      console.error('Error loading pending reductions:', e);
    }
  };

  // Soumettre une demande de réduction (pour les apporteurs)
  const submitReductionRequest = async (): Promise<void> => {
    if (!editing || !isApporteur) return;

    const selectedConsumables = consumablesForModal.filter((c) => selectedConsumableIdsForModal.has(c.id));

    console.log('[Reduction Request] Selected consumables:', selectedConsumables.map(c => ({ id: c.id, title: c.title })));
    console.log('[Reduction Request] Product pricing state:', productPricing);

    // Préparer les items avec les réductions demandées
    const items = selectedConsumables
      .map(consumable => {
        const pricingData = getProductPricing(consumable.id);

        // Trouver le prix actuel dans le BPU
        const existingItem = editing.pricing_items?.find(pi => pi.consumable_id === consumable.id);
        const currentPrice = existingItem?.custom_price ?? consumable.base_price;

        // Check if price changed
        const priceChanged = Math.abs(pricingData.custom_price - currentPrice) > 0.001;

        // Check if tier configuration exists for this product
        const newTierRequests = requestedTiers.filter(t => t.consumable_id === consumable.id);
        const existingTiers = pricingData.tiers || [];
        const productTiers = newTierRequests.length > 0 ? newTierRequests : existingTiers;
        const hasTierChanges = productTiers.length > 0;

        console.log(`[Reduction Request] Product ${consumable.title}:`, {
          currentPrice,
          requestedPrice: pricingData.custom_price,
          priceChanged,
          hasTierChanges,
          isActive: pricingData.is_active
        });

        return {
          consumable,
          pricingData,
          currentPrice,
          priceChanged,
          hasTierChanges,
          productTiers
        };
      })
      .filter(item => {
        // Keep if active AND (price changed OR tier changes exist)
        return item.pricingData.is_active && (item.priceChanged || item.hasTierChanges);
      })
      .map(item => ({
        consumable_id: item.consumable.id,
        requested_price: item.pricingData.custom_price,
        requested_discount_percentage: item.pricingData.discount_percent > 0 ? item.pricingData.discount_percent : undefined,
        requested_tiers: item.hasTierChanges ? item.productTiers : undefined,
        includes_tier_changes: item.hasTierChanges,
      }));

    console.log('[Reduction Request] Final items to submit:', items);

    if (items.length === 0) {
      showError('Aucune modification de prix ou de paliers détectée. Modifiez au moins un prix ou configurez des paliers avant de soumettre.');
      return;
    }

    try {
      await api.post('/price-reduction-requests/bulk', {
        client_id: editing.client_id,
        client_pricing_id: editing.id,
        items,
        request_reason: reductionRequestReason || undefined,
      });
      showSuccess(`Demande de réduction envoyée pour ${items.length} produit${items.length > 1 ? 's' : ''}`);
      setShowModal(false);
      setReductionRequestReason('');
      await load();
    } catch (e: any) {
      console.error('Error submitting reduction request:', e);
      showError(e.response?.data?.detail || 'Erreur lors de l\'envoi de la demande');
    }
  };

  // Approuver une demande de réduction (pour les admins)
  const approveReductionRequest = async (requestId: number): Promise<void> => {
    try {
      await api.post(`/price-reduction-requests/${requestId}/approve`);
      showSuccess('Demande de réduction approuvée');
      await loadPendingReductions();
      await load();
    } catch (e: any) {
      console.error('Error approving reduction:', e);
      showError(e.response?.data?.detail || 'Erreur lors de l\'approbation');
    }
  };

  // Rejeter une demande de réduction (pour les admins)
  const rejectReductionRequest = async (requestId: number): Promise<void> => {
    const confirmed = await showConfirm({
      title: 'Rejeter la demande',
      message: 'Êtes-vous sûr de vouloir rejeter cette demande de réduction ?',
      confirmText: 'Rejeter',
      cancelText: 'Annuler',
      severity: 'warning'
    });

    if (!confirmed) return;

    try {
      await api.post(`/price-reduction-requests/${requestId}/reject`);
      showSuccess('Demande de réduction rejetée');
      await loadPendingReductions();
    } catch (e: any) {
      console.error('Error rejecting reduction:', e);
      showError(e.response?.data?.detail || 'Erreur lors du rejet');
    }
  };

  // Handle URL parameters for Client pre-selection and create mode
  useEffect(() => {
    const clientId = searchParams.get('client_id');
    const createMode = searchParams.get('create');

    if (clientId && buyers.length > 0) {
      const buyerIdNum = parseInt(clientId, 10);
      const Client = buyers.find(b => b.id === buyerIdNum);

      if (Client) {
        if (createMode === 'true') {
          console.log('Opening create modal for Client:', Client.company_name);
          // Reset form to create mode with pre-selected Client
          setEditing(null);
          setForm({
            client_id: buyerIdNum,
            name: '',
            description: '',
            payment_term: '30 jours fin de mois',
            pricing_type: 'custom_price',
            custom_price: 0,
            discount_percent: 0,
          });
          setSelectedConsumableIds(new Set());
          setConsumablesSearch('');
          setExistingPricingForModal({});
          resetProductPricing();
          setShowModal(true);
          // Clear URL params after handling
          setSearchParams({});
        } else {
          // Filter to show only this Client's BPUs
          setSearch(Client.company_name);
          // Clear URL params after handling
          setSearchParams({});
        }
      }
    }
  }, [buyers, searchParams, setSearchParams]);

  const openViewModal = async (item: BuyerPricingListItem): Promise<void> => {
    setViewingBpu(item);
    setShowViewModal(true);
    setLoadingDetails(true);

    try {
      const response = await api.get(`/client-pricing/${item.id}`);
      const fullBpu: ClientPricing = response.data;
      setBpuDetails(fullBpu);
    } catch (e) {
      console.error('Error loading BPU details:', e);
      showError('Erreur lors du chargement des détails du BPU');
    } finally {
      setLoadingDetails(false);
    }
  };

  const closeViewModal = (): void => {
    setShowViewModal(false);
    setViewingBpu(null);
    setBpuDetails(null);
  };

  const handleCardClick = (item: BuyerPricingListItem): void => {
    // Always open view modal for all users
    openViewModal(item);
  };


  const activeClientIdForModal = editing ? editing.client_id : form.client_id;
  const activeClientForModal: Client | undefined = activeClientIdForModal
    ? (editing?.Client || buyers.find((c) => c.id === activeClientIdForModal))
    : undefined;
  const activeClientBrandNormalized = (activeClientForModal?.marque || '').trim().toUpperCase();
  const consumablesForModal = consumables.filter((c) => {
    if (!activeClientBrandNormalized) return true;
    return ((c.marque || '').trim().toUpperCase() === activeClientBrandNormalized);
  });

  const consumablesForModalIdSet = new Set(consumablesForModal.map((c) => c.id));
  const selectedConsumableIdsForModal = new Set(
    [...selectedConsumableIds].filter((id) => consumablesForModalIdSet.has(id))
  );

  useEffect(() => {
    if (!showModal) return;
    if (!activeClientBrandNormalized) return;

    const allowedIds = new Set(
      consumables
        .filter((c) => ((c.marque || '').trim().toUpperCase() === activeClientBrandNormalized))
        .map((c) => c.id)
    );

    setSelectedConsumableIds((prev) => new Set([...prev].filter((id) => allowedIds.has(id))));
  }, [showModal, activeClientBrandNormalized, consumables]);

  // Load unit types when editing a BPU
  useEffect(() => {
    if (showModal && editing) {
      loadUnitTypes(editing.id);
    }
  }, [showModal, editing]);

  // Helper functions for multi-product selection
  const toggleConsumableSelection = (id: number): void => {
    const newSelected = new Set(selectedConsumableIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedConsumableIds(newSelected);
  };

  const selectAllConsumables = (): void => {
    const filtered = consumablesForModal.filter(c =>
      c.title.toLowerCase().includes(consumablesSearch.toLowerCase())
    );
    setSelectedConsumableIds(new Set(filtered.map(c => c.id)));
  };

  const deselectAllConsumables = (): void => {
    setSelectedConsumableIds(new Set());
  };

  const updateProductPricing = (consumableId: number, pricing: {
    pricing_type: 'custom_price' | 'discount';
    custom_price: number;
    discount_percent: number;
    reduction_mode: 'percent' | 'amount';
    is_active: boolean;
  }): void => {
    setProductPricing(prev => ({
      ...prev,
      [consumableId]: pricing
    }));
  };

  const getProductPricing = (consumableId: number) => {
    const consumable = consumables.find(c => c.id === consumableId);
    const basePrice = Number(consumable?.base_price ?? 0);
    return productPricing[consumableId] || {
      pricing_type: 'custom_price' as const,
      custom_price: basePrice,
      discount_percent: 0,
      reduction_mode: 'percent' as const,
      is_active: true
    };
  };

  // Calcul en temps réel : met à jour le prix client quand on change la réduction
  // @ts-expect-error - Unused function kept for future use
  const handleReductionChange = (consumableId: number, value: number, mode: 'percent' | 'amount'): void => {
    const consumable = consumables.find(c => c.id === consumableId);
    if (!consumable) return;

    const basePrice = Number(consumable.base_price ?? 0);
    const currentPricing = getProductPricing(consumableId);

    let newCustomPrice: number;
    let newDiscountPercent: number;

    if (mode === 'percent') {
      newDiscountPercent = Math.min(100, Math.max(0, value));
      newCustomPrice = Number((basePrice * (1 - newDiscountPercent / 100)).toFixed(2));
    } else {
      // Mode montant : la valeur est le montant à déduire
      const deduction = Math.min(basePrice, Math.max(0, value));
      newCustomPrice = Number((basePrice - deduction).toFixed(2));
      newDiscountPercent = basePrice > 0 ? Number(((deduction / basePrice) * 100).toFixed(2)) : 0;
    }

    updateProductPricing(consumableId, {
      ...currentPricing,
      custom_price: newCustomPrice,
      discount_percent: newDiscountPercent,
      reduction_mode: mode
    });
  };

  // Calcul en temps réel : met à jour la réduction quand on change le prix client
  const handleCustomPriceChange = (consumableId: number, newPrice: number): void => {
    const consumable = consumables.find(c => c.id === consumableId);
    if (!consumable) return;

    const basePrice = Number(consumable.base_price ?? 0);
    const currentPricing = getProductPricing(consumableId);

    const clampedPrice = Math.max(0, newPrice);
    const newDiscountPercent = basePrice > 0 ? Number((((basePrice - clampedPrice) / basePrice) * 100).toFixed(2)) : 0;

    updateProductPricing(consumableId, {
      ...currentPricing,
      custom_price: clampedPrice,
      discount_percent: Math.max(0, newDiscountPercent)
    });
  };

  // Toggle actif/inactif pour un produit (may be used later)
  // const toggleProductActive = (consumableId: number): void => {
  //   const currentPricing = getProductPricing(consumableId);
  //   updateProductPricing(consumableId, {
  //     ...currentPricing,
  //     is_active: !currentPricing.is_active
  //   });
  // };

  // Toggle mode de réduction (% ou €) (may be used later)
  // const toggleReductionMode = (consumableId: number): void => {
  //   const currentPricing = getProductPricing(consumableId);
  //   const newMode = currentPricing.reduction_mode === 'percent' ? 'amount' : 'percent';
  //   updateProductPricing(consumableId, {
  //     ...currentPricing,
  //     reduction_mode: newMode
  //   });
  // };

  const resetProductPricing = (): void => {
    setProductPricing({});
  };

  const applyGlobalValue = (): void => {
    const rawValue: string = globalApplyValue.replace(',', '.').trim();
    const parsed: number = Number.parseFloat(rawValue);

    if (Number.isNaN(parsed)) {
      return;
    }

    const value: number = parsed;
    const clampedValue: number = globalPricingMode === 'percent'
      ? Math.min(100, Math.max(0, value))
      : Math.max(0, value);

    if (globalPricingMode === 'percent') {
      applyGlobalDiscount(clampedValue);
    } else {
      applyGlobalFixedReduction(clampedValue);
    }

    setGlobalApplyValue('');
  };

  // Load unit types for the current BPU
  const loadUnitTypes = async (bpuId: number): Promise<void> => {
    setLoadingUnitTypes(true);
    try {
      const response = await api.get(`/client-pricing/${bpuId}/unit-types`);
      setUnitTypes(response.data.unit_types || []);
    } catch (error) {
      console.error('Error loading unit types:', error);
      showError('Erreur lors du chargement des types d\'unités');
    } finally {
      setLoadingUnitTypes(false);
    }
  };

  // Handle unit type selection
  const handleUnitTypeClick = (unitType: UnitType): void => {
    setSelectedUnitType(unitType);
    setShowUnitTypeTierModal(true);
  };

  // Handle successful tier application
  const handleUnitTypeTierSuccess = (): void => {
    if (editing) {
      loadUnitTypes(editing.id);
      // Reload the modal to refresh tier data
      setShowModal(false);
      setTimeout(() => {
        setEditing(editing);
        setShowModal(true);
      }, 100);
    }
  };

  // Apply global discount percentage to all selected products
  const applyGlobalDiscount = (discountPercent: number): void => {
    const newPricing: typeof productPricing = {};
    selectedConsumableIdsForModal.forEach(consumableId => {
      const consumable = consumables.find(c => c.id === consumableId);
      if (!consumable) return;

      const basePrice = Number(consumable.base_price ?? 0);
      const newCustomPrice = Number((basePrice * (1 - discountPercent / 100)).toFixed(2));
      const currentPricing = getProductPricing(consumableId);

      newPricing[consumableId] = {
        ...currentPricing,
        custom_price: newCustomPrice,
        discount_percent: discountPercent,
        reduction_mode: 'percent'
      };
    });
    setProductPricing(prev => ({ ...prev, ...newPricing }));
  };

  // Apply global fixed price reduction to all selected products
  const applyGlobalFixedReduction = (reductionAmount: number): void => {
    const newPricing: typeof productPricing = {};
    selectedConsumableIdsForModal.forEach(consumableId => {
      const consumable = consumables.find(c => c.id === consumableId);
      if (!consumable) return;

      const basePrice = Number(consumable.base_price ?? 0);
      const newCustomPrice = Math.max(0, Number((basePrice - reductionAmount).toFixed(2)));
      const newDiscountPercent = basePrice > 0 ? Number(((reductionAmount / basePrice) * 100).toFixed(2)) : 0;
      const currentPricing = getProductPricing(consumableId);

      newPricing[consumableId] = {
        ...currentPricing,
        custom_price: newCustomPrice,
        discount_percent: newDiscountPercent,
        reduction_mode: 'amount'
      };
    });
    setProductPricing(prev => ({ ...prev, ...newPricing }));
  };

  const save = async (): Promise<void> => {
    if (!form.client_id || selectedConsumableIdsForModal.size === 0) return;

    try {
      const selectedConsumables = consumablesForModal.filter((c) => selectedConsumableIdsForModal.has(c.id));

      // Prepare pricing items for the BPU - only include active products
      const pricingItems = selectedConsumables
        .filter(consumable => {
          const pricingData = getProductPricing(consumable.id);
          return pricingData.is_active;
        })
        .map(consumable => {
          const basePrice = Number(consumable.base_price ?? 0);
          if (basePrice <= 0) {
            throw new Error(`Prix Marché manquant pour le consommable: ${consumable.title}`);
          }

          const pricingData = getProductPricing(consumable.id);

          return {
            consumable_id: consumable.id,
            custom_price: pricingData.custom_price,
            discount_percentage: pricingData.discount_percent > 0 ? pricingData.discount_percent : undefined,
            tiers: pricingData.tiers && pricingData.tiers.length > 0 ? pricingData.tiers : undefined,
          };
        });

      if (editing) {
        // Update existing BPU
        await api.patch(`/client-pricing/${editing.id}`, {
          name: form.name || undefined,
          description: form.description || undefined,
          payment_term: form.payment_term || undefined,
          pricing_items: pricingItems,
        });
        showSuccess(`BPU "${editing.name}" mis à jour avec ${pricingItems.length} produit${pricingItems.length > 1 ? 's' : ''}`);
      } else {
        // Create new BPU
        const Client = buyers.find(b => b.id === form.client_id);
        const bpuName = form.name || Client?.company_name;
        await api.post('/client-pricing', {
          client_id: form.client_id,
          name: bpuName, // Will be prefixed with "BPU | " by backend
          description: form.description || `Tarif personnalisé pour ${pricingItems.length} produit${pricingItems.length > 1 ? 's' : ''}`,
          is_active: true,
          payment_term: form.payment_term || '30 jours fin de mois',
          pricing_items: pricingItems,
        });
        showSuccess(`Nouveau BPU créé avec ${pricingItems.length} produit${pricingItems.length > 1 ? 's' : ''}`);
      }

      setShowModal(false);
      await load();
    } catch (e: any) {
      console.error('Save error:', e);
      showError(e.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    }
  };

  const restore = async (item: BuyerPricingListItem): Promise<void> => {
    const confirmed = await showConfirm({
      title: 'Restaurer le BPU',
      message: `Êtes-vous sûr de vouloir restaurer le BPU "${item.name}" ? Il repassera en statut brouillon.`,
      confirmText: 'Restaurer',
      cancelText: 'Annuler',
      severity: 'info'
    });

    if (!confirmed) return;

    try {
      await api.post(`/client-pricing/${item.id}/restore`);
      await load();
      showSuccess(`BPU "${item.name}" restauré en brouillon`);
    } catch (e: any) {
      console.error('Restore error:', e);
      showError(e.response?.data?.detail || 'Erreur lors de la restauration');
    }
  };

  const del = async (item: BuyerPricingListItem): Promise<void> => {
    const isArchived = item.status === 'archived';
    const confirmed = await showConfirm({
      title: isArchived ? 'Supprimer définitivement' : 'Supprimer le BPU',
      message: isArchived
        ? `Êtes-vous sûr de vouloir supprimer DÉFINITIVEMENT le BPU "${item.name}" ? Cette action est irréversible.`
        : `Êtes-vous sûr de vouloir supprimer le BPU "${item.name}" ? Il sera déplacé dans la corbeille (Supprimés).`,
      confirmText: isArchived ? 'Supprimer définitivement' : 'Supprimer',
      cancelText: 'Annuler',
      severity: 'error'
    });

    if (!confirmed) return;

    try {
      await api.delete(`/client-pricing/${item.id}`);
      await load();
      showSuccess(isArchived ? `BPU "${item.name}" supprimé définitivement` : `BPU "${item.name}" déplacé dans la corbeille`);
    } catch (e) {
      console.error('Delete error:', e);
      showError('Erreur lors de la suppression');
    }
  };

  const validateBpu = async (item: BuyerPricingListItem): Promise<void> => {
    const confirmed = await showConfirm({
      title: 'Valider le BPU',
      message: `Êtes-vous sûr de vouloir valider le BPU "${item.name}" ?`,
      confirmText: 'Valider',
      cancelText: 'Annuler',
      severity: 'warning'
    });

    if (!confirmed) return;

    try {
      await api.post(`/client-pricing/${item.id}/validate`);
      await load();
      showSuccess(`BPU "${item.name}" validé et bloqué`);
    } catch (e: any) {
      console.error('Validate error:', e);
      showError(e.response?.data?.detail || 'Erreur lors de la validation');
    }
  };



  // Helper function to get card colors based on status
  const getCardColors = (status: 'draft' | 'validated' | 'archived') => {
    const colors = {
      draft: {
        border: 'border-yellow-300 dark:border-yellow-700',
        hoverBorder: 'hover:border-yellow-400 dark:hover:border-yellow-600',
        bg: 'bg-yellow-50/50 dark:bg-yellow-950/20',
        iconBg: 'from-yellow-100 to-yellow-200 dark:from-yellow-900/30 dark:to-yellow-800/30',
        iconColor: 'text-yellow-600 dark:text-yellow-400'
      },
      validated: {
        border: 'border-green-300 dark:border-green-700',
        hoverBorder: 'hover:border-green-400 dark:hover:border-green-600',
        bg: 'bg-green-50/50 dark:bg-green-950/20',
        iconBg: 'from-green-100 to-green-200 dark:from-green-900/30 dark:to-green-800/30',
        iconColor: 'text-green-600 dark:text-green-400'
      },
      archived: {
        border: 'border-gray-300 dark:border-gray-700',
        hoverBorder: 'hover:border-gray-400 dark:hover:border-gray-600',
        bg: 'bg-gray-50/50 dark:bg-gray-950/20',
        iconBg: 'from-gray-100 to-gray-200 dark:from-gray-900/30 dark:to-gray-800/30',
        iconColor: 'text-gray-600 dark:text-gray-400'
      }
    };
    return colors[status] || colors.draft;
  };

  // Helper function to render status badge
  const renderStatusBadge = (status: 'draft' | 'validated' | 'archived', version?: number) => {
    const badges = {
      draft: {
        bg: 'bg-yellow-100 dark:bg-yellow-900/30',
        text: 'text-yellow-800 dark:text-yellow-300',
        label: 'Brouillon'
      },
      validated: {
        bg: 'bg-green-100 dark:bg-green-900/30',
        text: 'text-green-800 dark:text-green-300',
        label: 'Validé'
      },
      archived: {
        bg: 'bg-red-100 dark:bg-red-900/30',
        text: 'text-red-800 dark:text-red-300',
        label: 'Supprimé'
      }
    };

    const badge = badges[status] || badges.draft;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        {status === 'validated' && <Lock className="h-3 w-3" />}
        {badge.label}
        {version && version > 1 && <span className="ml-1">v{version}</span>}
      </span>
    );
  };

  const handleSort = (field: 'reference_number' | 'client_name' | 'name' | 'status' | 'items_count' | 'created_at') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getBuyerTypeLabel = (type: string): string => {
    switch (type) {
      case 'independent': return 'Indépendants';
      case 'centrale': return 'Centrales d\'achat';
      case 'pharmacie': return 'Pharmacies';
      case 'parapharmacie': return 'Parapharmacies';
      case 'magasin_bio': return 'Magasins Bio';
      case 'therapeute': return 'Thérapeutes';
      default: return type;
    }
  };

  const getStatusLabel = (status: string): string => {
    switch (status) {
      case 'draft': return 'Brouillons';
      case 'validated': return 'Validés';
      case 'archived': return 'Supprimés';
      default: return status;
    }
  };

  const activeFilters = useMemo(() => {
    const tags: Array<{ key: string; label: string; onClear: () => void }> = [];
    if (search.trim()) tags.push({ key: 'search', label: `Recherche: ${search}`, onClear: () => setSearch('') });
    if (statusFilter !== 'all') tags.push({ key: 'status', label: `Statut: ${getStatusLabel(statusFilter)}`, onClear: () => setStatusFilter('all') });
    if (buyerTypeFilter !== 'all') tags.push({ key: 'buyerType', label: `Type client: ${getBuyerTypeLabel(buyerTypeFilter)}`, onClear: () => setBuyerTypeFilter('all') });
    if (selectedCentralFilter) tags.push({ key: 'central', label: `Centrale: ${selectedCentralFilter}`, onClear: () => setSelectedCentralFilter(null) });
    return tags;
  }, [search, statusFilter, buyerTypeFilter, selectedCentralFilter]);

  const resetAllFilters = (): void => {
    setSearch('');
    localStorage.removeItem(searchStorageKey);
    localStorage.removeItem(filterStorageKey);
    setStatusFilter('all');
    setBuyerTypeFilter('all');
    setSelectedCentralFilter(null);
    setSortField('created_at');
    setSortDirection('desc');
    setPage(1);
  };

  const filtered = items.filter(item => {
    // Les clients (non-managers) ne doivent voir que les BPU validés
    if (!isManager && item.status !== 'validated') return false;

    // Exclure les BPU hérités (ceux qui ont un source_bpu_id)
    if (item.source_bpu_id !== null) return false;

    // Status filter
    if (statusFilter !== 'all' && item.status !== statusFilter) return false;

    // Client type filter (independent vs central)
    if (buyerTypeFilter !== 'all') {
      // Special handling for centrale/central - accept both
      if (buyerTypeFilter === 'centrale') {
        if (!item.Client || (item.Client.client_type !== 'centrale' && item.Client.client_type !== 'central')) {
          return false;
        }
      } else {
        // For other types, exact match required
        if (!item.Client || item.Client.client_type !== buyerTypeFilter) {
          return false;
        }
      }
    }

    // Search filter is handled server-side
    return true;
  }).sort((a, b) => {
    let aVal: any;
    let bVal: any;

    switch (sortField) {
      case 'reference_number':
        aVal = a.reference_number || '';
        bVal = b.reference_number || '';
        break;
      case 'client_name':
        aVal = (a.client_name || a.Client?.company_name || '').toLowerCase();
        bVal = (b.client_name || b.Client?.company_name || '').toLowerCase();
        break;
      case 'name':
        aVal = (a.name || '').toLowerCase();
        bVal = (b.name || '').toLowerCase();
        break;
      case 'status':
        aVal = a.status || '';
        bVal = b.status || '';
        break;
      case 'items_count':
        aVal = a.items_count || 0;
        bVal = b.items_count || 0;
        break;
      case 'created_at':
        aVal = new Date(a.created_at).getTime();
        bVal = new Date(b.created_at).getTime();
        break;
      default:
        return 0;
    }

    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const stats = {
    total,
    draft: items.filter(i => i.status === 'draft').length,
    validated: items.filter(i => i.status === 'validated').length,
    archived: items.filter(i => i.status === 'archived').length,
    active: items.filter(i => i.is_active).length,
    totalProducts: items.reduce((sum, item) => sum + item.items_count, 0),
  } as const;

  return (
    <div className="h-full overflow-auto md:overflow-hidden md:flex md:flex-col bg-gradient-to-br from-slate-50 via-blue-50/20 to-slate-50 dark:from-slate-950 dark:via-blue-950/10 dark:to-slate-950">
      <ModuleHeader
        icon={Euro}
        title="Bordereau de Prix"
        subtitle={
          <>
            {stats.total} BPU • {stats.active} actif{stats.active > 1 ? 's' : ''}
          </>
        }
        actions={
          isManager && (
            <div className="flex gap-2">
              {/* Bouton pour voir les demandes de réduction en attente (admin seulement) */}
              {/* {
                isAdmin && (
                  <button
                    onClick={() => {
                      setShowReductionRequestsModal(true);
                      loadPendingReductions();
                    }}
                    className="relative flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white rounded-lg shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all duration-200 font-medium"
                  >
                    <Clock className="h-4 w-4" />
                    Demandes de réduction
                    {pendingReductionsCount > 0 && (
                      <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                        {pendingReductionsCount}
                      </span>
                    )}
                  </button>
                )
              } */}
              <button
                onClick={() => {
                  setShowInheritedModal(true);
                  loadInheritedBpus();
                }}
                className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 text-sm bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 font-medium"
              >
                <Euro className="h-4 w-4" />
                BPU Hérités
              </button>
            </div >
          )
        }
      />

      {/* Status Filter Tabs */}
      <div className="px-3 py-2 sm:px-6 sm:py-4">
        <div className="flex items-center justify-between mb-2 sm:mb-4">
          <div className="flex bg-slate-100 dark:bg-slate-800 rounded-lg p-0.5 sm:p-1 w-full sm:w-auto overflow-x-auto">
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-md text-[11px] sm:text-sm font-medium transition-all duration-200 whitespace-nowrap ${statusFilter === 'all'
                ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-sm'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100'
                }`}
            >
              Tous ({stats.total})
            </button>
            <button
              onClick={() => setStatusFilter('draft')}
              className={`px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-md text-[11px] sm:text-sm font-medium transition-all duration-200 whitespace-nowrap ${statusFilter === 'draft'
                ? 'bg-white dark:bg-slate-700 text-yellow-700 dark:text-yellow-400 shadow-sm'
                : 'text-slate-600 dark:text-slate-400 hover:text-yellow-600 dark:hover:text-yellow-400'
                }`}
            >
              Brouillons ({stats.draft})
            </button>
            <button
              onClick={() => setStatusFilter('validated')}
              className={`px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-md text-[11px] sm:text-sm font-medium transition-all duration-200 whitespace-nowrap ${statusFilter === 'validated'
                ? 'bg-white dark:bg-slate-700 text-green-700 dark:text-green-400 shadow-sm'
                : 'text-slate-600 dark:text-slate-400 hover:text-green-600 dark:hover:text-green-400'
                }`}
            >
              Validés ({stats.validated})
            </button>
            <button
              onClick={() => setStatusFilter('archived')}
              className={`px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-md text-[11px] sm:text-sm font-medium transition-all duration-200 whitespace-nowrap ${statusFilter === 'archived'
                ? 'bg-white dark:bg-slate-700 text-red-700 dark:text-red-400 shadow-sm'
                : 'text-slate-600 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400'
                }`}
            >
              Supprimés ({stats.archived})
            </button>
          </div>
        </div>
      </div>

      {/* Desktop: Search and View Mode */}
      <div className="hidden md:flex px-6 py-3 gap-4 items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher par référence, client, nom de BPU..." className="w-full h-10 pl-10 pr-4 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
        </div>

        {/* Client Type Filter */}
        <select
          value={buyerTypeFilter}
          onChange={(e) => setBuyerTypeFilter(e.target.value as 'all' | 'independent' | 'centrale' | 'pharmacie' | 'parapharmacie' | 'magasin_bio' | 'therapeute')}
          className="h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
        >
          <option value="all">Tous clients</option>
          <option value="independent">Indépendants</option>
          <option value="centrale">Centrales d'achat</option>
          <option value="pharmacie">Pharmacies</option>
          <option value="parapharmacie">Parapharmacies</option>
          <option value="magasin_bio">Magasins Bio</option>
          <option value="therapeute">Thérapeutes</option>
        </select>

        <div className="flex items-center gap-1 border border-slate-300 dark:border-slate-600 rounded-lg p-1">
          <button onClick={() => setViewMode('list')} className={`p-2 rounded ${viewMode === 'list' ? 'bg-purple-100 dark:bg-purple-950/30 text-purple-700' : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800'}`}>
            <ListIcon className="h-4 w-4" />
          </button>
          <button onClick={() => setViewMode('table')} className={`p-2 rounded ${viewMode === 'table' ? 'bg-purple-100 dark:bg-purple-950/30 text-purple-700' : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800'}`}>
            <TableIcon className="h-4 w-4" />
          </button>
        </div>

        {viewMode === 'table' && (
          <button
            onClick={() => setShowColumnConfigurator(true)}
            className="h-10 px-4 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-2"
            title="Gérer les colonnes"
          >
            <Settings className="h-4 w-4" />
            <span className="hidden sm:inline">Colonnes</span>
          </button>
        )}

        <button
          onClick={resetAllFilters}
          className="h-10 px-4 rounded-lg border bg-red-50 dark:bg-red-950/20 border-red-300 dark:border-red-800 text-sm text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-950/30 transition-colors flex items-center gap-2"
        >
          <RefreshCw className="h-4 w-4" />
          Réinitialiser
        </button>
      </div>

      {/* Mobile: Search and View Mode */}
      <div className="flex flex-col gap-2 px-3 py-2 md:hidden">
        <div className="relative w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher..." className="w-full h-9 pl-9 pr-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500" />
        </div>
        <div className="flex items-center gap-2">
          <select
            value={buyerTypeFilter}
            onChange={(e) => setBuyerTypeFilter(e.target.value as 'all' | 'independent' | 'centrale' | 'pharmacie' | 'parapharmacie' | 'magasin_bio' | 'therapeute')}
            className="h-8 flex-1 min-w-0 px-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500"
          >
            <option value="all">Tous clients</option>
            <option value="independent">Indépendants</option>
            <option value="centrale">Centrales</option>
            <option value="pharmacie">Pharmacies</option>
            <option value="parapharmacie">Parapharmacies</option>
            <option value="magasin_bio">Magasins Bio</option>
            <option value="therapeute">Thérapeutes</option>
          </select>
          <button
            onClick={resetAllFilters}
            className="h-8 px-2.5 rounded-lg border bg-red-50 dark:bg-red-950/20 border-red-300 dark:border-red-800 text-xs text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-950/30 transition-colors flex items-center gap-1"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Réinitialiser
          </button>
        </div>
      </div>

      {activeFilters.length > 0 && (
        <div className="px-6 pb-3 flex flex-wrap items-center gap-2 text-sm">
          <span className="text-slate-600 dark:text-slate-300 font-semibold">Filtres actifs:</span>
          {activeFilters.map((tag) => (
            <span key={tag.key} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300 shadow-sm dark:bg-emerald-950/50 dark:text-emerald-200 dark:border-emerald-700">
              {tag.label}
              <button
                type="button"
                onClick={tag.onClear}
                className="inline-flex items-center justify-center rounded-full p-0.5 text-emerald-700 hover:bg-emerald-200 hover:text-emerald-900 dark:text-emerald-200 dark:hover:bg-emerald-900/50"
                aria-label={`Supprimer ${tag.label}`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Content */}
      <div className="md:flex-1 md:overflow-auto px-3 pb-4 sm:px-6 sm:pb-6 md:min-h-0">
        {busy ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-500 dark:text-slate-400">
            <Package className="h-16 w-16 mb-4 opacity-50" />
            <p className="text-lg font-medium">Aucun BPU trouvé</p>
            <p className="text-sm mt-2">Créez votre premier tarif personnalisé</p>
          </div>
        ) : viewMode === 'list' ? (
          <div className="space-y-2 sm:space-y-3">
            {filtered.map((item, idx) => {
              const cardColors = getCardColors(item.status);
              return (
                <div
                  key={item.id}
                  onClick={() => handleCardClick(item)}
                  className={`p-2.5 sm:p-3.5 rounded-xl border-2 ${cardColors.border} ${cardColors.bg} ${cardColors.hoverBorder} hover:shadow-lg transition-all duration-300 ease-out animate-in fade-in slide-in-from-left-4 cursor-pointer`}
                  style={{ animationDelay: `${idx * 30}ms`, animationFillMode: 'backwards' }}
                >
                  {/* Desktop layout */}
                  <div className="hidden sm:flex items-center gap-4">
                    <div className={`w-12 h-12 bg-gradient-to-br ${cardColors.iconBg} rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm`}>
                      <Euro className={`h-6 w-6 ${cardColors.iconColor}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300">
                          {item.reference_number}
                        </span>
                        {renderStatusBadge(item.status, item.version)}
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-400">
                        <Building2 className="h-3.5 w-3.5 text-purple-500 shrink-0" />
                        <span className="font-medium">{item.client_name || item.Client?.company_name || 'Client'}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 px-4 border-l border-slate-200 dark:border-slate-700 shrink-0">
                      <div className="text-center">
                        <div className="text-xs text-slate-500 dark:text-slate-400 mb-0.5">Produits</div>
                        <div className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{item.items_count}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-slate-500 dark:text-slate-400 mb-0.5">Commandes</div>
                        <div className="text-lg font-bold text-cyan-600 dark:text-cyan-400">{item.orders_count}</div>
                      </div>
                    </div>
                    {isManager && (
                      <div className="flex gap-1.5 pl-3 border-l border-slate-200 dark:border-slate-700 shrink-0">
                        {item.status === 'draft' && (
                          <>
                            <button onClick={(e) => { e.stopPropagation(); validateBpu(item); }} disabled={!canValidateBpu} className={`p-2 rounded-lg transition-all duration-200 ${canValidateBpu ? 'text-green-600 hover:bg-green-50 dark:hover:bg-green-950/30' : 'text-gray-400 cursor-not-allowed'}`} title={canValidateBpu ? 'Valider et bloquer' : 'Validation réservée au super admin'}><Lock className="h-4 w-4" /></button>
                            <button onClick={(e) => { e.stopPropagation(); del(item); }} className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-all duration-200" title="Supprimer"><Trash2 className="h-4 w-4" /></button>
                          </>
                        )}
                        {item.status === 'validated' && (
                          <span className="text-xs text-green-600 dark:text-green-400 font-medium flex items-center gap-1"><Lock className="h-3 w-3" />Validé</span>
                        )}
                        {item.status === 'archived' && (
                          <>
                            <button onClick={(e) => { e.stopPropagation(); restore(item); }} className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-all duration-200" title="Restaurer"><RotateCcw className="h-4 w-4" /></button>
                            <button onClick={(e) => { e.stopPropagation(); del(item); }} className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-all duration-200" title="Supprimer définitivement"><Trash2 className="h-4 w-4" /></button>
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Mobile layout */}
                  <div className="flex sm:hidden gap-2.5">
                    <div className={`w-10 h-10 bg-gradient-to-br ${cardColors.iconBg} rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm`}>
                      <Euro className={`h-5 w-5 ${cardColors.iconColor}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300">
                          {item.reference_number}
                        </span>
                        {renderStatusBadge(item.status, item.version)}
                      </div>
                      <div className="flex items-center gap-1.5 text-[11px] text-slate-600 dark:text-slate-400 mb-1.5">
                        <Building2 className="h-3 w-3 text-purple-500 shrink-0" />
                        <span className="font-medium">{item.client_name || item.Client?.company_name || 'Client'}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 text-[11px]">
                          <span className="text-slate-500">Produits : <span className="font-bold text-emerald-600">{item.items_count}</span></span>
                          <span className="text-slate-500">Commandes : <span className="font-bold text-cyan-600">{item.orders_count}</span></span>
                        </div>
                        {isManager && (
                          <div className="flex gap-1 shrink-0">
                            {item.status === 'draft' && (
                              <>
                                <button onClick={(e) => { e.stopPropagation(); validateBpu(item); }} disabled={!canValidateBpu} className={`p-1.5 rounded-lg ${canValidateBpu ? 'text-green-600' : 'text-gray-400 cursor-not-allowed'}`}><Lock className="h-3.5 w-3.5" /></button>
                                <button onClick={(e) => { e.stopPropagation(); del(item); }} className="p-1.5 text-red-600 rounded-lg"><Trash2 className="h-3.5 w-3.5" /></button>
                              </>
                            )}
                            {item.status === 'validated' && (
                              <span className="text-[10px] text-green-600 font-medium flex items-center gap-0.5"><Lock className="h-3 w-3" />Validé</span>
                            )}
                            {item.status === 'archived' && (
                              <>
                                <button onClick={(e) => { e.stopPropagation(); restore(item); }} className="p-1.5 text-blue-600 rounded-lg"><RotateCcw className="h-3.5 w-3.5" /></button>
                                <button onClick={(e) => { e.stopPropagation(); del(item); }} className="p-1.5 text-red-600 rounded-lg"><Trash2 className="h-3.5 w-3.5" /></button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : viewMode === 'table' ? (
          <div className="bg-white dark:bg-slate-900 rounded-xl border-2 border-slate-200 dark:border-slate-700 overflow-hidden shadow-lg flex flex-col" style={{ maxHeight: 'calc(100vh - 320px)' }}>
            <div className="overflow-x-auto overflow-y-auto flex-1">
              <table className="w-full min-w-[800px]">
                <thead className="bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30 border-b-2 border-emerald-200 dark:border-emerald-800 sticky top-0 z-10">
                  <tr>
                    {columns.filter(col => col.visible && col.key !== 'actions').map(col => renderTableHeader(col))}
                    {visibleColumnKeys.includes('actions') && (
                      <th className="px-4 py-3 text-right text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {filtered.map((item) => (
                    <tr key={item.id} onClick={() => handleCardClick(item)} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors cursor-pointer">
                      {columns.filter(col => col.visible && col.key !== 'actions').map(col => renderTableCell(col, item))}
                      {visibleColumnKeys.includes('actions') && (
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            {isManager && (
                              <>
                                {item.status === 'draft' && (
                                  <>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); validateBpu(item); }}
                                      disabled={!canValidateBpu}
                                      className={`p-1.5 rounded-lg transition-colors ${canValidateBpu
                                        ? 'text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20'
                                        : 'text-gray-400 cursor-not-allowed'}`}
                                      title={canValidateBpu ? 'Valider et bloquer' : 'Validation réservée au super admin'}
                                    >
                                      <Lock className="h-4 w-4" />
                                    </button>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); del(item); }}
                                      className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                      title="Supprimer"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </>
                                )}
                                {item.status === 'validated' && (
                                  <span className="text-xs text-green-600 dark:text-green-400 font-medium flex items-center gap-1">
                                    <Lock className="h-3 w-3" />
                                    Validé
                                  </span>
                                )}
                                {item.status === 'archived' && (
                                  <>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); restore(item); }}
                                      className="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                                      title="Restaurer"
                                    >
                                      <RotateCcw className="h-4 w-4" />
                                    </button>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); del(item); }}
                                      className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                      title="Supprimer définitivement"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        {/* Pagination Controls */}
        {total > pageSize && (
          <div className="flex items-center justify-between px-4 py-4 mt-6 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
            <div className="text-sm text-slate-500 dark:text-slate-400">
              Affichage de <span className="font-semibold text-slate-700 dark:text-slate-200">{(page - 1) * pageSize + 1}</span> à{' '}
              <span className="font-semibold text-slate-700 dark:text-slate-200">{Math.min(page * pageSize, total)}</span> sur{' '}
              <span className="font-semibold text-slate-700 dark:text-slate-200">{total}</span> BPU
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Précédent
              </button>
              <div className="flex items-center gap-1">
                {Array.from({ length: pages }, (_, i) => i + 1)
                  .filter((pageNum) => {
                    if (pages <= 7) return true;
                    return pageNum === 1 || pageNum === pages || (pageNum >= page - 2 && pageNum <= page + 2);
                  })
                  .map((pageNum, i, arr) => (
                    <React.Fragment key={pageNum}>
                      {i > 0 && arr[i - 1] !== pageNum - 1 && (
                        <span className="px-2 text-slate-400">...</span>
                      )}
                      <button
                        onClick={() => setPage(pageNum)}
                        className={`w-10 h-10 flex items-center justify-center text-sm font-medium rounded-lg transition-colors ${page === pageNum
                          ? 'bg-emerald-600 text-white'
                          : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                          }`}
                      >
                        {pageNum}
                      </button>
                    </React.Fragment>
                  ))}
              </div>
              <button
                onClick={() => setPage((p) => Math.min(pages, p + 1))}
                disabled={page === pages}
                className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </div>

      {/* BPU Modification Modal (for editing) */}
      {showModal && editing && (
        <BpuModificationModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          editing={editing}
          consumables={consumables}
          onSave={async (pricingData) => {
            try {
              await api.patch(`/client-pricing/${editing.id}`, {
                name: form.name || undefined,
                description: form.description || undefined,
                payment_term: form.payment_term || undefined,
                pricing_items: pricingData,
              });
              showSuccess(`BPU "${editing.name}" mis à jour avec ${pricingData.length} produit${pricingData.length > 1 ? 's' : ''}`);
              setShowModal(false);
              setEditing(null);
              load();
            } catch (error: any) {
              showError(error.response?.data?.detail || 'Erreur lors de la mise à jour du BPU');
            }
          }}
        />
      )}

      {/* BPU Creation Modal */}
      <BpuCreationModal
        isOpen={showModal && !editing}
        onClose={() => setShowModal(false)}
        buyers={buyers}
        consumables={consumablesForModal}
        selectedConsumableIds={selectedConsumableIdsForModal}
        onConsumableToggle={toggleConsumableSelection}
        onSelectAll={selectAllConsumables}
        onDeselectAll={deselectAllConsumables}
        globalPricingMode={globalPricingMode}
        onModeSwitch={handleModeSwitch}
        globalApplyValue={globalApplyValue}
        onGlobalApplyValueChange={setGlobalApplyValue}
        onApplyGlobalValue={applyGlobalValue}
        consumablesSearch={consumablesSearch}
        onSearchChange={setConsumablesSearch}
        productPricing={productPricing}
        onTierConfigOpen={(consumableId) => {
          setSelectedProductForTiers(consumableId);
          setShowTierConfig(true);
        }}
        clientId={form.client_id}
        onClientChange={(clientId) => setForm({ ...form, client_id: clientId })}
        onSave={save}
      />

      {/* Old modal code - to be removed */}
      {false && showModal && !editing && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto animate-in zoom-in-95 slide-in-from-bottom-4 duration-300">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700 sticky top-0 bg-white dark:bg-slate-900 z-10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
                  <Package className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">Nouveau BPU</h2>
                </div>
              </div>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Informations générales */}
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <span className="text-sm text-slate-500 dark:text-slate-400">Client</span>
                  <select
                    className="w-full mt-1 px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600"
                    value={form.client_id || ''}
                    onChange={(e) => setForm({ ...form, client_id: e.target.value ? parseInt(e.target.value, 10) : null })}
                  >
                    <option value="">Sélectionner un client</option>
                    {buyers.map(Client => (
                      <option key={Client.id} value={Client.id}>{Client.company_name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Liste des produits - Style inspiré de la vue détail */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-lg flex items-center gap-2">
                    <Package className="h-5 w-5 text-emerald-600" />
                    Produits ({selectedConsumableIdsForModal.size} sélectionné{selectedConsumableIdsForModal.size > 1 ? 's' : ''})
                  </h3>
                  <div className="flex gap-2">
                    <button
                      onClick={selectAllConsumables}
                      className="text-xs text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 font-medium"
                    >
                      Tout sélectionner
                    </button>
                    <span className="text-slate-300">|</span>
                    <button
                      onClick={deselectAllConsumables}
                      className="text-xs text-slate-600 hover:text-slate-700 dark:text-slate-400 font-medium"
                    >
                      Tout désélectionner
                    </button>
                  </div>
                </div>

                {/* Unit-based pricing section */}
                {editing && unitTypes.length > 0 && (
                  <div className="mb-4 p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
                    <h4 className="text-sm font-semibold text-orange-900 dark:text-orange-100 mb-3">
                      Appliquer par unité
                    </h4>
                    <div className="space-y-2">
                      {unitTypes.map((unitType, index) => (
                        <button
                          key={index}
                          onClick={() => handleUnitTypeClick(unitType)}
                          className="w-full flex items-center justify-between p-3 bg-white dark:bg-slate-800 border border-orange-200 dark:border-orange-700 rounded-lg hover:bg-orange-50 dark:hover:bg-orange-900/30 transition-colors group"
                        >
                          <div className="text-left">
                            <div className="font-medium text-slate-900 dark:text-white">
                              {unitType.unit_name}
                            </div>
                            <div className="text-xs text-slate-500 dark:text-slate-400">
                              Lot: {unitType.batch_size} | {unitType.product_count} produits
                            </div>
                          </div>
                          <ArrowRight className="w-5 h-5 text-orange-600 group-hover:translate-x-1 transition-transform" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Switch Tab: Pourcentage / UG (Tier-based pricing) */}
                <div className="flex items-center gap-4 mb-4 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Mode de réduction :</span>
                  <div className="flex bg-slate-200 dark:bg-slate-700 rounded-lg p-1">
                    <button
                      onClick={() => handleModeSwitch('percent')}
                      className={`px-6 py-2 text-sm font-medium rounded-md transition-all ${globalPricingMode === 'percent'
                        ? 'bg-emerald-600 text-white shadow-sm'
                        : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
                        }`}
                      title="Remise en pourcentage - Efface les données UG"
                    >
                      % Pourcentage
                    </button>
                    <button
                      onClick={() => handleModeSwitch('free_units')}
                      className={`px-6 py-2 text-sm font-medium rounded-md transition-all ${globalPricingMode === 'free_units'
                        ? 'bg-purple-600 text-white shadow-sm'
                        : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
                        }`}
                      title="Unités Gratuites - Efface les données de pourcentage"
                    >
                      UG Unités Gratuites
                    </button>
                  </div>

                  {/* Quick apply global discount/reduction */}
                  {selectedConsumableIdsForModal.size > 0 && (
                    <div className="flex items-center gap-2 ml-auto">
                      <span className="text-xs text-slate-500">Appliquer à tous sélectionnés :</span>
                      <input
                        type="text"
                        placeholder={globalPricingMode === 'percent' ? '10' : globalPricingMode === 'free_units' ? '6' : '5,00'}
                        className="w-20 px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-right"
                        value={globalApplyValue}
                        onChange={(e) => setGlobalApplyValue(e.target.value)}
                        onFocus={(e) => e.target.select()}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            applyGlobalValue();
                          }
                        }}
                      />
                      <button
                        type="button"
                        onClick={applyGlobalValue}
                        disabled={!globalApplyValue.trim()}
                        className="px-3 py-1 text-xs font-medium rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:bg-slate-400 disabled:cursor-not-allowed"
                      >
                        Appliquer
                      </button>
                      <span className="text-sm text-slate-500">
                        {globalPricingMode === 'percent' ? '%' : globalPricingMode === 'free_units' ? 'UG' : '€'}
                      </span>
                    </div>
                  )}
                </div>

                {/* Barre de recherche */}
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input
                    type="text"
                    value={consumablesSearch}
                    onChange={(e) => setConsumablesSearch(e.target.value)}
                    placeholder="Rechercher un produit..."
                    className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm"
                  />
                </div>

                {/* Tableau des produits - Affiche TOUS les produits avec toggle d'inclusion */}
                <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden max-h-[400px] overflow-y-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 dark:bg-slate-800 sticky top-0">
                      <tr>
                        <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase w-16">Inclus</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Produit</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Prix Marché</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Paliers</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Prix Client</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                      {consumablesForModal
                        .filter(c => c.title.toLowerCase().includes(consumablesSearch.toLowerCase()))
                        .map((consumable) => {
                          const isSelected = selectedConsumableIds.has(consumable.id);
                          const pricing = getProductPricing(consumable.id);
                          const basePrice = Number(consumable.base_price ?? 0);
                          // const reductionAmount = basePrice - pricing.custom_price;

                          return (
                            <tr
                              key={consumable.id}
                              className={`transition-all ${isSelected ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'opacity-50 bg-slate-50 dark:bg-slate-800/30'}`}
                            >
                              {/* Toggle Inclus/Exclus */}
                              <td className="px-4 py-3 text-center">
                                <button
                                  onClick={() => toggleConsumableSelection(consumable.id)}
                                  className={`w-10 h-5 rounded-full transition-colors relative ${isSelected ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600'}`}
                                >
                                  <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${isSelected ? 'left-5' : 'left-0.5'}`} />
                                </button>
                              </td>
                              {/* Nom du produit */}
                              <td className={`px-4 py-3 text-sm ${isSelected ? 'text-slate-900 dark:text-slate-100' : 'text-slate-400'}`}>
                                {consumable.title}
                              </td>
                              {/* Prix Marché */}
                              <td className="px-4 py-3 text-sm text-right text-slate-600 dark:text-slate-400">
                                {basePrice.toFixed(2)} €
                              </td>
                              {/* Paliers (Tiers) Configuration */}
                              <td className="px-4 py-3 text-center">
                                {isSelected ? (
                                  <button
                                    onClick={() => {
                                      setSelectedProductForTiers(consumable.id);
                                      setShowTierConfig(true);
                                    }}
                                    className="px-3 py-1 text-xs font-medium rounded bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/50 transition-colors"
                                  >
                                    {(() => {
                                      // Filter tiers by current mode
                                      const relevantTiers = pricing.tiers?.filter(tier => {
                                        if (globalPricingMode === 'percent') {
                                          return tier.discount_percentage !== undefined && tier.discount_percentage !== null;
                                        } else {
                                          return tier.free_units !== undefined && tier.free_units !== null;
                                        }
                                      }) || [];

                                      return relevantTiers.length > 0
                                        ? `${relevantTiers.length} palier${relevantTiers.length > 1 ? 's' : ''}`
                                        : 'Configurer';
                                    })()}
                                  </button>
                                ) : (
                                  <span className="text-slate-400 text-sm">—</span>
                                )}
                              </td>
                              {/* Prix client (calculé en temps réel) */}
                              <td className="px-4 py-3 text-right">
                                {isSelected ? (
                                  <div className="flex items-center justify-end">
                                    <input
                                      type="text"
                                      inputMode="decimal"
                                      className="w-20 px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-right font-medium text-emerald-600"
                                      value={pricing.custom_price.toFixed(2)}
                                      onChange={(e) => handleCustomPriceChange(consumable.id, parseFloat(e.target.value) || 0)}
                                      onFocus={(e) => e.target.select()}
                                    />
                                    <span className="ml-1 text-slate-500">€</span>
                                  </div>
                                ) : (
                                  <span className="text-slate-400 text-sm">{basePrice.toFixed(2)} €</span>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      }
                    </tbody>
                    {selectedConsumableIdsForModal.size > 0 && (
                      <tfoot className="bg-slate-50 dark:bg-slate-800">
                        <tr>
                          <td colSpan={4} className="px-4 py-3 text-right font-bold text-slate-900 dark:text-slate-100">
                            Total produits sélectionnés
                          </td>
                          <td className="px-4 py-3 text-right font-bold text-emerald-600 text-lg">
                            {selectedConsumableIdsForModal.size}
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>

              {/* Demandes en attente de l'apporteur */}
              {isApporteur && editing && (
                <PendingRequestsList
                  requests={myPendingRequests}
                  consumables={consumables}
                />
              )}

              {/* Raison de la demande (pour apporteurs en mode édition) */}
              {isApporteur && editing && (
                <ApporteurReductionRequest
                  reductionRequestReason={reductionRequestReason}
                  onReasonChange={setReductionRequestReason}
                  requestedTiersCount={requestedTiers.length}
                />
              )}

              {/* Footer */}
              <div className="flex justify-end gap-2 pt-4 border-t border-slate-200 dark:border-slate-700">
                <button onClick={() => setShowModal(false)} className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                  Annuler
                </button>

                {/* Bouton différent selon le rôle */}
                {isApporteur && editing ? (
                  <button
                    onClick={submitReductionRequest}
                    disabled={selectedConsumableIdsForModal.size === 0}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-orange-600 text-white hover:bg-orange-700 disabled:bg-slate-400 transition-colors font-medium"
                  >
                    <Send className="h-4 w-4" />
                    Soumettre la demande
                  </button>
                ) : (
                  <button
                    onClick={save}
                    disabled={!form.client_id || selectedConsumableIdsForModal.size === 0}
                    className="px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:bg-slate-400 transition-colors font-medium"
                  >
                    {editing ? 'Enregistrer' : 'Créer le BPU'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )
      }

      {/* BPU View Modal */}
      <BpuViewModal
        isOpen={showViewModal}
        onClose={closeViewModal}
        bpuDetails={bpuDetails}
        viewingBpu={viewingBpu}
        buyers={buyers}
        loadingDetails={loadingDetails}
        canEdit={user?.role === 'super_admin'}
        onEdit={() => {
          if (!bpuDetails) return;
          const bpuToEdit = bpuDetails;
          closeViewModal();
          setEditing(bpuToEdit);
          setShowModal(true);
        }}
      />

      {/* Old view modal code - to be removed */}
      {false && showViewModal && viewingBpu && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={closeViewModal}>
          <div className="bg-white dark:bg-slate-900 rounded-xl max-w-5xl w-full max-h-[90vh] overflow-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="sticky top-0 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 p-6 flex items-center justify-between z-10">
              <div>
                <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Détails du BPU</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{viewingBpu?.reference_number}</p>
              </div>
              <div className="flex items-center gap-3">
                {(isAdmin || isApporteur) && bpuDetails && (
                  <button
                    onClick={() => {
                      setSelectedBpuForTiers(bpuDetails);
                      setShowBpuProductTierModal(true);
                    }}
                    className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center gap-2"
                  >
                    <Euro className="w-4 h-4" />
                    Configurer les paliers
                  </button>
                )}
                <button onClick={closeViewModal} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              {loadingDetails ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
                </div>
              ) : bpuDetails ? (
                <>
                  {/* Informations générales */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div>
                      <span className="text-sm text-slate-500 dark:text-slate-400">Client</span>
                      <p className="font-medium text-slate-900 dark:text-slate-100">
                        {bpuDetails?.Client?.company_name || viewingBpu?.Client?.company_name || buyers.find(b => b.id === bpuDetails?.client_id)?.company_name || `Client #${bpuDetails?.client_id}`}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm text-slate-500 dark:text-slate-400">Statut</span>
                      <div className="mt-1">
                        {bpuDetails?.status === 'validated' && (
                          <span className="inline-block px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 text-xs font-medium rounded-full">
                            ✓ Validé
                          </span>
                        )}
                        {bpuDetails?.status === 'draft' && (
                          <span className="inline-block px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 text-xs font-medium rounded-full">
                            ✏️ Brouillon
                          </span>
                        )}
                        {bpuDetails?.status === 'archived' && (
                          <span className="inline-block px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 text-xs font-medium rounded-full">
                            �️ Supprimé
                          </span>
                        )}
                      </div>
                    </div>
                    {bpuDetails?.description && (
                      <div className="col-span-2">
                        <span className="text-sm text-slate-500 dark:text-slate-400">Description</span>
                        <p className="text-sm text-slate-700 dark:text-slate-300">
                          {bpuDetails?.description &&
                            bpuDetails?.description?.includes('BPU créé automatiquement lors de la conversion du prospect')
                            ? `BPU créé automatiquement lors de la conversion du prospect ${bpuDetails?.Client?.company_name || viewingBpu?.Client?.company_name || buyers.find(b => b.id === bpuDetails?.client_id)?.company_name || ''}`
                            : bpuDetails?.description}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Liste des produits */}
                  <div>
                    <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                      <Package className="h-5 w-5 text-emerald-600" />
                      Produits ({bpuDetails?.pricing_items?.length || 0})
                    </h3>
                    <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-slate-50 dark:bg-slate-800">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Produit</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Prix Marché</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Réduction</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Prix Client</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                          {bpuDetails?.pricing_items?.map((item) => {
                            const consumable = consumables.find(c => c.id === item.consumable_id);
                            const basePrice = consumable?.base_price || 0;
                            const discount = item.discount_percentage || 0;
                            const customPrice = item.custom_price;
                            const savings = basePrice - customPrice;
                            const savingsPercent = basePrice > 0 ? ((savings / basePrice) * 100) : 0;

                            return (
                              <tr key={item.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                                <td className="px-4 py-3 text-sm text-slate-900 dark:text-slate-100">
                                  {consumable?.title || `Produit #${item.consumable_id}`}
                                </td>
                                <td className="px-4 py-3 text-sm text-right text-slate-600 dark:text-slate-400">
                                  {basePrice.toFixed(2)} {consumable?.currency || 'EUR'}
                                </td>
                                <td className="px-4 py-3 text-sm text-right">
                                  {discount > 0 ? (
                                    <span className="text-orange-600 dark:text-orange-400 font-medium">
                                      -{discount.toFixed(1)}%
                                    </span>
                                  ) : (
                                    <span className="text-emerald-600 dark:text-emerald-400 font-medium">
                                      -{savingsPercent.toFixed(1)}%
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-sm text-right font-medium text-emerald-600 dark:text-emerald-400">
                                  {customPrice.toFixed(2)} {consumable?.currency || 'EUR'}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot className="bg-slate-50 dark:bg-slate-800">
                          <tr>
                            <td colSpan={3} className="px-4 py-3 text-right font-bold text-slate-900 dark:text-slate-100">
                              Total produits
                            </td>
                            <td className="px-4 py-3 text-right font-bold text-emerald-600 text-lg">
                              {bpuDetails?.pricing_items?.length || 0}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  Aucune donnée disponible
                </div>
              )}
            </div>
          </div>
        </div>
      )
      }

      {/* Reduction Requests Modal */}
      <ReductionRequestsModal
        isOpen={showReductionRequestsModal}
        onClose={() => setShowReductionRequestsModal(false)}
        requests={pendingReductions}
        consumables={consumables}
        onApprove={approveReductionRequest}
        onReject={rejectReductionRequest}
      />

      {/* Old reduction requests modal code - to be removed */}
      {false && showReductionRequestsModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-950/20 dark:to-amber-950/20">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-orange-500 to-amber-600 rounded-lg">
                  <Clock className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">Demandes de réduction</h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {pendingReductions.filter(r => r.status === 'pending').length} demande{pendingReductions.filter(r => r.status === 'pending').length > 1 ? 's' : ''} en attente
                  </p>
                </div>
              </div>
              <button onClick={() => setShowReductionRequestsModal(false)} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
                <X className="h-5 w-5 text-slate-500" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto p-6">
              {pendingReductions.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-slate-500 dark:text-slate-400">
                  <CheckCircle2 className="h-16 w-16 mb-4 opacity-50 text-green-500" />
                  <p className="text-lg font-medium">Aucune demande en attente</p>
                  <p className="text-sm mt-2">Toutes les demandes de réduction ont été traitées</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingReductions.map((request) => {
                    const consumable = consumables.find(c => c.id === request.consumable_id);
                    const savingsPercent = request.requested_discount_percentage || 0;
                    const savingsAmount = request.original_price - request.requested_price;

                    return (
                      <div
                        key={request.id}
                        className={`bg-white dark:bg-slate-800 border rounded-lg p-4 ${request.status === 'pending'
                          ? 'border-orange-200 dark:border-orange-800'
                          : request.status === 'approved'
                            ? 'border-green-200 dark:border-green-800'
                            : 'border-red-200 dark:border-red-800'
                          }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="font-semibold text-slate-900 dark:text-slate-100">
                                {request.consumable_title || consumable?.title || `Produit #${request.consumable_id}`}
                              </h3>
                              <span className={`px-2 py-1 text-xs rounded-full ${request.status === 'pending'
                                ? 'bg-orange-100 text-orange-700 dark:bg-orange-950/30 dark:text-orange-400'
                                : request.status === 'approved'
                                  ? 'bg-green-100 text-green-700 dark:bg-green-950/30 dark:text-green-400'
                                  : 'bg-red-100 text-red-700 dark:bg-red-950/30 dark:text-red-400'
                                }`}>
                                {request.status === 'pending' ? 'En attente' : request.status === 'approved' ? 'Approuvé' : 'Rejeté'}
                              </span>
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-3">
                              <div>
                                <span className="text-slate-500 dark:text-slate-400">Client:</span>
                                <p className="font-medium text-slate-900 dark:text-slate-100">{request.client_name || `Client #${request.client_id}`}</p>
                              </div>
                              <div>
                                <span className="text-slate-500 dark:text-slate-400">Prix actuel:</span>
                                <p className="font-medium text-slate-900 dark:text-slate-100">{request.original_price.toFixed(2)} €</p>
                              </div>
                              <div>
                                <span className="text-slate-500 dark:text-slate-400">Prix demandé:</span>
                                <p className="font-medium text-emerald-600 dark:text-emerald-400">{request.requested_price.toFixed(2)} €</p>
                              </div>
                              <div>
                                <span className="text-slate-500 dark:text-slate-400">Réduction:</span>
                                <p className="font-medium text-orange-600 dark:text-orange-400">
                                  -{savingsPercent.toFixed(1)}% ({savingsAmount.toFixed(2)} €)
                                </p>
                              </div>
                            </div>

                            {request.request_reason && (
                              <div className="text-sm bg-slate-50 dark:bg-slate-900 rounded p-2 mb-3">
                                <span className="text-slate-500 dark:text-slate-400">Raison: </span>
                                <span className="text-slate-700 dark:text-slate-300">{request.request_reason}</span>
                              </div>
                            )}

                            {/* Display tier configuration if present */}
                            {request.includes_tier_changes && request.requested_tiers && request.requested_tiers.length > 0 && (
                              <div className="text-sm bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded p-3 mb-3">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="text-blue-700 dark:text-blue-300 font-medium">🎁 Configuration des paliers (UG)</span>
                                </div>
                                <div className="space-y-1">
                                  {request.requested_tiers.map((tier: any, idx: number) => (
                                    <div key={idx} className="text-xs text-blue-600 dark:text-blue-400">
                                      <span className="font-medium">Palier {tier.tier_number}:</span>
                                      {' '}À partir de {tier.min_quantity} unité{tier.min_quantity > 1 ? 's' : ''}
                                      {tier.max_quantity && ` jusqu'à ${tier.max_quantity}`}
                                      {tier.free_units && ` → ${tier.free_units} gratuit${tier.free_units > 1 ? 's' : ''}`}
                                      {tier.discount_percentage && ` → ${tier.discount_percentage}% de réduction`}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            <div className="text-xs text-slate-500 dark:text-slate-400">
                              Demandé par {request.requester_name || `Utilisateur #${request.requested_by}`} le {new Date(request.created_at).toLocaleDateString('fr-FR')}
                            </div>
                          </div>

                          {/* Actions (seulement pour les demandes en attente) */}
                          {request.status === 'pending' && (
                            <div className="flex gap-2 ml-4">
                              <button
                                onClick={() => approveReductionRequest(request.id)}
                                className="flex items-center gap-1 px-3 py-2 text-sm bg-green-50 hover:bg-green-100 dark:bg-green-950/30 dark:hover:bg-green-950/50 text-green-700 dark:text-green-400 rounded-lg transition-colors"
                              >
                                <CheckCircle2 className="h-4 w-4" />
                                Approuver
                              </button>
                              <button
                                onClick={() => rejectReductionRequest(request.id)}
                                className="flex items-center gap-1 px-3 py-2 text-sm bg-red-50 hover:bg-red-100 dark:bg-red-950/30 dark:hover:bg-red-950/50 text-red-700 dark:text-red-400 rounded-lg transition-colors"
                              >
                                <XCircle className="h-4 w-4" />
                                Rejeter
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700 flex justify-end">
              <button
                onClick={() => setShowReductionRequestsModal(false)}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )
      }

      {/* Modal BPU Hérités */}
      {
        showInheritedModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
              {/* Header */}
              <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg">
                    <Building2 className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">BPU Hérités</h2>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {inheritedBpus.length} BPU hérité{inheritedBpus.length > 1 ? 's' : ''} de centrales d'achat
                    </p>
                  </div>
                </div>
                <button onClick={() => setShowInheritedModal(false)} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
                  <X className="h-5 w-5 text-slate-500" />
                </button>
              </div>

              {/* Filtre par centrale */}
              <div className="px-6 py-3 border-b border-slate-200 dark:border-slate-700">
                <select
                  value={selectedCentralFilter || ''}
                  onChange={(e) => setSelectedCentralFilter(e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Toutes les centrales d'achat</option>
                  {Array.from(new Set(inheritedBpus.map(bpu => bpu.source_bpu_id).filter(Boolean)))
                    .map(sourceBpuId => {
                      const sourceBpu = items.find(item => item.id === sourceBpuId);
                      return sourceBpu ? (
                        <option key={sourceBpuId} value={sourceBpuId?.toString() || ''}>
                          {sourceBpu.Client?.company_name || `BPU #${sourceBpuId}`}
                        </option>
                      ) : null;
                    })}
                </select>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-auto p-6">
                {inheritedBpus.filter(bpu => !selectedCentralFilter || bpu.source_bpu_id === selectedCentralFilter).length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-64 text-slate-500 dark:text-slate-400">
                    <Building2 className="h-16 w-16 mb-4 opacity-50" />
                    <p className="text-lg font-medium">Aucun BPU hérité trouvé</p>
                    <p className="text-sm mt-2">Les BPU hérités sont créés automatiquement pour les clients membres</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {inheritedBpus
                      .filter(bpu => !selectedCentralFilter || bpu.source_bpu_id === selectedCentralFilter)
                      .map((item) => {
                        const sourceBpu = items.find(i => i.id === item.source_bpu_id);
                        return (
                          <div key={item.id} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4 hover:shadow-md transition-shadow">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <h3 className="font-semibold text-slate-900 dark:text-slate-100">{item.name}</h3>
                                  <span className={`px-2 py-1 text-xs rounded-full ${item.status === 'validated'
                                    ? 'bg-green-100 text-green-700 dark:bg-green-950/30 dark:text-green-400'
                                    : item.status === 'draft'
                                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-950/30 dark:text-blue-400'
                                      : 'bg-red-100 text-red-700 dark:bg-red-950/30 dark:text-red-400'
                                    }`}>
                                    {item.status === 'validated' ? 'Validé' : item.status === 'draft' ? 'Brouillon' : 'Supprimé'}
                                  </span>
                                  {item.is_active && (
                                    <span className="px-2 py-1 text-xs rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-400">
                                      Actif
                                    </span>
                                  )}
                                </div>
                                <div className="text-sm text-slate-600 dark:text-slate-400 space-y-1">
                                  <p><strong>Client membre:</strong> {item.Client?.company_name || 'N/A'}</p>
                                  <p><strong>Centrale d'achat:</strong> {sourceBpu?.Client?.company_name || 'N/A'}</p>
                                  <p><strong>Version:</strong> {item.version}</p>
                                  <p><strong>Produits:</strong> {item.items_count}</p>
                                  <p><strong>Commandes:</strong> {item.orders_count}</p>
                                  <p><strong>Terme de règlement:</strong> {item.payment_term}</p>
                                </div>
                              </div>
                              <button
                                onClick={() => openViewModal(item)}
                                className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-50 hover:bg-blue-100 dark:bg-blue-950/30 dark:hover:bg-blue-950/50 text-blue-700 dark:text-blue-400 rounded-lg transition-colors"
                              >
                                <Eye className="h-4 w-4" />
                                Voir
                              </button>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700 flex justify-end">
                <button
                  onClick={() => setShowInheritedModal(false)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg transition-colors"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Tier Configuration Modal */}
      {
        showTierConfig && selectedProductForTiers && (
          <TierConfigModal
            isOpen={showTierConfig}
            onClose={() => {
              setShowTierConfig(false);
              setSelectedProductForTiers(null);
            }}
            productName={consumablesForModal.find(c => c.id === selectedProductForTiers)?.title || 'Produit'}
            basePrice={Number(consumablesForModal.find(c => c.id === selectedProductForTiers)?.base_price || 0)}
            mode={globalPricingMode}
            existingTiers={productPricing[selectedProductForTiers]?.tiers || []}
            onSave={(tiers) => {
              // Save tiers to product pricing state
              setProductPricing(prev => ({
                ...prev,
                [selectedProductForTiers]: {
                  ...prev[selectedProductForTiers],
                  tiers
                }
              }));
            }}
          />
        )
      }

      {/* Unit Type Tier Modal */}
      {editing && (
        <UnitTypeTierModal
          isOpen={showUnitTypeTierModal}
          onClose={() => {
            setShowUnitTypeTierModal(false);
            setSelectedUnitType(null);
          }}
          unitType={selectedUnitType}
          bpuId={editing.id}
          onSuccess={handleUnitTypeTierSuccess}
        />
      )}

      {/* BPU Product Tier Modal */}
      {selectedBpuForTiers && (
        <BpuProductTierModal
          isOpen={showBpuProductTierModal}
          onClose={() => {
            setShowBpuProductTierModal(false);
            setSelectedBpuForTiers(null);
            setSelectedProductForTiers(null);
          }}
          bpuId={selectedBpuForTiers.id}
          bpuName={selectedBpuForTiers.name}
          products={(selectedBpuForTiers.pricing_items || []).map(item => {
            const consumable = consumables.find(c => c.id === item.consumable_id);
            return {
              id: item.id,
              consumable_id: item.consumable_id,
              custom_price: item.custom_price,
              tiers: item.tiers,
              consumable: consumable ? {
                id: consumable.id,
                title: consumable.title,
                marque: consumable.marque || undefined,
                unit: consumable.unit || undefined
              } : undefined
            };
          })}
          initialProductId={selectedProductForTiers || undefined}
          onSuccess={async () => {
            // Reload BPU details to show updated tiers
            if (viewingBpu) {
              try {
                const response = await api.get(`/client-pricing/${viewingBpu.id}`);
                const fullBpu: ClientPricing = response.data;
                setBpuDetails(fullBpu);
              } catch (e) {
                console.error('Error reloading BPU details:', e);
              }
            }
            // Reload BPU list
            load();
            showSuccess('Paliers mis à jour avec succès');
          }}
        />
      )}

      {/* Column Configurator Modal */}
      <ColumnConfigurator
        isOpen={showColumnConfigurator}
        onClose={() => setShowColumnConfigurator(false)}
        columns={columns}
        onChange={setColumns}
        storageKey="bpu_table_columns"
      />
    </div >
  );
};

export default ClientPricingTailwind;
